import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import tipOfferApi from "api/tipOfferApi";
import { CADDY_ROUTES } from "constants/routesPath";
import {
  OFFER_STATUS,
  formatCurrency,
  formatOfferDate as formatDate,
  formatOfferTime as formatTime,
  statusLabel,
} from "utils/tipOffer";

/**
 * Caddy "Book me a caddie" offer view (internal name: tip-offer).
 *
 * Entered via the LINE broadcast deep link → LIFF route carrying the offer id
 * (`:id` route param). Fetches the offer via `tipOfferApi.getOffer`, shows the
 * course / date / tee time / tip amount and status, and — when the offer is
 * open — presents an explicit read-and-accept-the-tip control that applies via
 * `tipOfferApi.applyToOffer`. Non-open / expired / duplicate / unavailable
 * errors returned by the backend are mapped to clear bilingual (TH/EN)
 * messages, and a confirmation is shown on success.
 *
 * Requirements: 9.6, 9.7, 9.9, 9.10
 */

// ===== Bilingual feature label (user-facing name is "Book me a caddie") =====

const FEATURE_LABEL = {
  th: "จองแคดดี้ให้ฉัน",
  en: "Book me a caddie",
};

// ===== Styled Components (mirrors the insurance-landing conventions) =====

const PageWrapper = styled.div`
  min-height: 100vh;
  background: #f5f7fa;
  padding: 16px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
`;

const Header = styled.header`
  text-align: center;
  margin-bottom: 20px;
`;

const Title = styled.h1`
  font-size: 20px;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0 0 4px 0;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: #666;
  margin: 0;
`;

const Card = styled.div`
  background: #ffffff;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  color: #ffffff;
  background: ${(props) => props.$color || "#8c8c8c"};
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }
`;

const DetailLabel = styled.span`
  font-size: 13px;
  color: #666;
`;

const DetailValue = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #1a1a2e;
  text-align: right;
  margin-left: 8px;
`;

const TipRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 12px 0 4px 0;
`;

const TipLabel = styled.span`
  font-size: 13px;
  color: #666;
`;

const TipValue = styled.span`
  font-size: 26px;
  font-weight: 700;
  color: #eb2f96;
`;

const AcceptRow = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 0;
  cursor: pointer;
`;

const AcceptCheckbox = styled.input`
  width: 22px;
  height: 22px;
  margin-top: 2px;
  flex-shrink: 0;
`;

const AcceptText = styled.span`
  font-size: 13px;
  color: #333;
  line-height: 1.4;
`;

const ApplyButton = styled.button`
  display: block;
  width: 100%;
  min-height: 48px;
  min-width: 48px;
  padding: 14px 24px;
  background: #2d6a4f;
  color: #ffffff;
  font-size: 16px;
  font-weight: 600;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  margin-top: 8px;
  transition: background 0.2s ease;

  &:hover:not(:disabled) {
    background: #1b4332;
  }

  &:active:not(:disabled) {
    background: #143d2b;
  }

  &:disabled {
    background: #b7c7bf;
    cursor: not-allowed;
  }
`;

const InfoBanner = styled.p`
  font-size: 14px;
  color: #666;
  text-align: center;
  margin: 8px 0 0 0;
`;

const ErrorBanner = styled.p`
  font-size: 14px;
  color: #d32f2f;
  background: #fdecea;
  border-radius: 8px;
  padding: 12px;
  margin: 8px 0 0 0;
  text-align: center;
`;

const SuccessBanner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  background: #e8f5e9;
  border-radius: 12px;
  padding: 20px 16px;
  text-align: center;
`;

const SuccessTitle = styled.p`
  font-size: 18px;
  font-weight: 700;
  color: #2d6a4f;
  margin: 0;
`;

const SuccessText = styled.p`
  font-size: 14px;
  color: #333;
  margin: 0;
`;

const StateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  padding: 24px;
  text-align: center;
  gap: 16px;
`;

const StateText = styled.p`
  font-size: 15px;
  color: #666;
  margin: 0;
`;

const RetryButton = styled.button`
  min-height: 44px;
  min-width: 44px;
  padding: 12px 24px;
  background: #2d6a4f;
  color: #ffffff;
  font-size: 14px;
  font-weight: 500;
  border: none;
  border-radius: 8px;
  cursor: pointer;

  &:hover {
    background: #1b4332;
  }
`;

/**
 * Map a backend error (typed error envelope
 * `{ error: { code, message: { th, en } } }`, keyed by HTTP status) to a
 * bilingual message object for display. Falls back to the server-provided
 * bilingual message, then to a generic message.
 *
 * Requirements: 9.7 (non-open / expired / duplicate / unavailable mapping)
 */
function mapApplyError(error) {
  const status = error?.response?.status;
  const serverMsg = error?.response?.data?.error?.message;

  // 409 conflict covers: not-open, expired-window, duplicate application, and
  // caddy-unavailable. Prefer the specific server-provided bilingual message
  // (which distinguishes these cases) when available.
  if (status === 409 || status === 400) {
    if (serverMsg && (serverMsg.th || serverMsg.en)) {
      return {
        th: serverMsg.th || serverMsg.en,
        en: serverMsg.en || serverMsg.th,
      };
    }
    return {
      th: "ไม่สามารถสมัครข้อเสนอนี้ได้ (อาจปิดรับ หมดเวลา หรือสมัครไปแล้ว)",
      en: "Unable to apply (offer may be closed, expired, or already applied)",
    };
  }

  if (status === 404) {
    return {
      th: "ไม่พบข้อเสนอนี้",
      en: "This offer was not found",
    };
  }

  if (serverMsg && (serverMsg.th || serverMsg.en)) {
    return {
      th: serverMsg.th || serverMsg.en,
      en: serverMsg.en || serverMsg.th,
    };
  }

  return {
    th: "เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง",
    en: "Something went wrong. Please try again.",
  };
}

// ===== Main container =====

export default function CaddyTipOffer() {
  const params = useParams();
  const navigate = useNavigate();
  const offerId = params.id;

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [offer, setOffer] = useState(null);

  const [accepted, setAccepted] = useState(false);
  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState(null);
  const [applied, setApplied] = useState(false);

  const fetchOffer = useCallback(async () => {
    if (!offerId) {
      setLoadError(true);
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError(false);
    try {
      const response = await tipOfferApi.getOffer(offerId);
      setOffer(response?.data?.data || null);
    } catch (error) {
      console.error("Failed to load tip offer:", error);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [offerId]);

  useEffect(() => {
    fetchOffer();
  }, [fetchOffer]);

  const handleApply = useCallback(async () => {
    if (!accepted || applying) return;
    setApplying(true);
    setApplyError(null);
    try {
      // The caddy explicitly accepts the offered tip; the backend records the
      // accepted amount from the offer itself.
      await tipOfferApi.applyToOffer(offerId, { accept_tip: true });
      setApplied(true);
    } catch (error) {
      console.error("Failed to apply to tip offer:", error);
      setApplyError(mapApplyError(error));
      // Refresh offer state so a status change (e.g. now matched/expired) is
      // reflected after a conflict.
      fetchOffer();
    } finally {
      setApplying(false);
    }
  }, [accepted, applying, offerId, fetchOffer]);

  // ----- Loading -----
  if (loading) {
    return (
      <PageWrapper>
        <StateContainer>
          <StateText>กำลังโหลด... (Loading...)</StateText>
        </StateContainer>
      </PageWrapper>
    );
  }

  // ----- Load error / not found -----
  if (loadError || !offer) {
    return (
      <PageWrapper>
        <StateContainer>
          <StateText>
            ไม่สามารถโหลดข้อเสนอได้ กรุณาลองใหม่อีกครั้ง
            <br />
            Unable to load the offer. Please try again.
          </StateText>
          <RetryButton onClick={fetchOffer} aria-label="ลองใหม่ (Retry)">
            ลองใหม่ (Retry)
          </RetryButton>
        </StateContainer>
      </PageWrapper>
    );
  }

  const status = statusLabel(OFFER_STATUS, offer.status);
  const myStatus = offer.my_application_status;
  // Older API responses lack `can_apply`; fall back to "open and not applied".
  const canApply =
    offer.can_apply ?? (offer.status === "open" && !myStatus);

  return (
    <PageWrapper>
      <Header>
        <Title>{FEATURE_LABEL.th}</Title>
        <Subtitle>{FEATURE_LABEL.en}</Subtitle>
      </Header>

      <Card>
        <DetailRow>
          <DetailLabel>สถานะ (Status)</DetailLabel>
          <StatusBadge $color={status.color}>
            {status.th} ({status.en})
          </StatusBadge>
        </DetailRow>
        <DetailRow>
          <DetailLabel>สนามกอล์ฟ (Golf course)</DetailLabel>
          <DetailValue>{offer.golf_course_name || "-"}</DetailValue>
        </DetailRow>
        <DetailRow>
          <DetailLabel>วันที่ (Date)</DetailLabel>
          <DetailValue>{formatDate(offer.tf_date)}</DetailValue>
        </DetailRow>
        <DetailRow>
          <DetailLabel>เวลาทีออฟ (Tee time)</DetailLabel>
          <DetailValue>{formatTime(offer.tf_time)}</DetailValue>
        </DetailRow>
        <TipRow>
          <TipLabel>ทิปที่เสนอ (Offered tip)</TipLabel>
          <TipValue>{formatCurrency(offer.tip_amount)} บาท (THB)</TipValue>
        </TipRow>
      </Card>

      {applied || myStatus === "applied" ? (
        <Card>
          <SuccessBanner role="status" aria-live="polite">
            <SuccessTitle>สมัครแล้ว (Applied)</SuccessTitle>
            <SuccessText>
              คุณได้ยอมรับทิปและสมัครเรียบร้อยแล้ว รอนักกอล์ฟเลือกแคดดี้
              <br />
              You have accepted the tip and applied. Please wait for the golfer
              to select a caddie.
            </SuccessText>
          </SuccessBanner>
        </Card>
      ) : myStatus === "selected" ? (
        <Card>
          <SuccessBanner role="status">
            <SuccessTitle>คุณได้รับเลือก (You were selected)</SuccessTitle>
            <SuccessText>
              นักกอล์ฟเลือกคุณสำหรับงานนี้ ดูรายละเอียดได้ที่รายการงาน
              <br />
              The golfer chose you for this job. See it in your job list.
            </SuccessText>
            <ApplyButton
              type="button"
              onClick={() => navigate(CADDY_ROUTES.JOB_LIST)}
            >
              ไปที่รายการงาน (Go to job list)
            </ApplyButton>
          </SuccessBanner>
        </Card>
      ) : myStatus === "not_selected" ? (
        <Card>
          <InfoBanner>
            นักกอล์ฟไม่ได้เลือกคุณสำหรับข้อเสนอนี้
            <br />
            You were not selected for this offer.
          </InfoBanner>
        </Card>
      ) : canApply ? (
        <Card>
          <AcceptRow htmlFor="accept-tip">
            <AcceptCheckbox
              id="accept-tip"
              type="checkbox"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              aria-label="ยอมรับทิปที่เสนอ (Accept the offered tip)"
            />
            <AcceptText>
              ฉันได้อ่านและยอมรับทิปจำนวน {formatCurrency(offer.tip_amount)} บาท
              สำหรับงานนี้
              <br />I have read and accept the offered tip of{" "}
              {formatCurrency(offer.tip_amount)} THB for this job.
            </AcceptText>
          </AcceptRow>

          <ApplyButton
            type="button"
            onClick={handleApply}
            disabled={!accepted || applying}
            aria-label="สมัครและยอมรับทิป (Apply and accept tip)"
          >
            {applying
              ? "กำลังสมัคร... (Applying...)"
              : "สมัคร (Apply)"}
          </ApplyButton>

          {applyError && (
            <ErrorBanner role="alert">
              {applyError.th}
              <br />
              {applyError.en}
            </ErrorBanner>
          )}
        </Card>
      ) : offer.status === "open" ? (
        <Card>
          <InfoBanner>
            คุณไม่ว่างหรือไม่อยู่ในสนามนี้สำหรับช่วงเวลาทีออฟนี้
            <br />
            You are not available at this course for this tee time.
          </InfoBanner>
          {applyError && (
            <ErrorBanner role="alert">
              {applyError.th}
              <br />
              {applyError.en}
            </ErrorBanner>
          )}
        </Card>
      ) : (
        <Card>
          <InfoBanner>
            ข้อเสนอนี้ไม่เปิดรับสมัครแล้ว
            <br />
            This offer is no longer open for applications.
          </InfoBanner>
          {applyError && (
            <ErrorBanner role="alert">
              {applyError.th}
              <br />
              {applyError.en}
            </ErrorBanner>
          )}
        </Card>
      )}
    </PageWrapper>
  );
}
